/**
 * VectorShortcuts Web — frontend state machine.
 *
 * Four states: idle → loading → ready OR error.
 * Never leaves the page. Pinned prompt on submit, card slide-in on ready,
 * retry button on retryable errors.
 */

// --- Types ------------------------------------------------------------------

type State = "idle" | "loading" | "ready" | "error" | "locked";

interface BuildOK {
  status: "ok";
  name: string;
  summary: string;
  action_count: number;
  shortcut_url: string;
  install_url: string;
}

interface BuildError {
  status: "error";
  code: string;
  retryable: boolean;
  message: string;
  retry_after?: number;
}

type BuildResponse = BuildOK | BuildError;

// --- Config -----------------------------------------------------------------

const MAX_CHARS = 800;
const API_BASE = resolveApiBase();
const CACHE_KEY = "vs-last-build-v1";
const BACKEND_KEY = "vs-backend-v1";
const FRIEND_KEY = resolveFriendKey();

type Backend = "mlx" | "codex";

// --- Element refs -----------------------------------------------------------

const $ = <T extends Element>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as unknown as T;
};

const main = document.querySelector<HTMLElement>(".vs-main")!;
const promptCard = $<HTMLElement>("promptCard");
const promptInput = $<HTMLTextAreaElement>("prompt");
const charCounter = $<HTMLElement>("charCounter");
const buildButton = $<HTMLButtonElement>("buildButton");
const revealSection = $<HTMLElement>("revealSection");
const phaseText = $<HTMLElement>("phaseText");
const chipRow = $<HTMLElement>("chipRow");
const progress = $<HTMLElement>("progress");
const progressFill = $<HTMLElement>("progressFill");
const resultCard = $<HTMLElement>("resultCard");
const resultName = $<HTMLElement>("resultName");
const resultSummary = $<HTMLElement>("resultSummary");
const resultActionCount = $<HTMLElement>("resultActionCount");
const installLink = $<HTMLAnchorElement>("installLink");
const downloadLink = $<HTMLAnchorElement>("downloadLink");
const errorCard = $<HTMLElement>("errorCard");
const errorTitle = $<HTMLElement>("errorTitle");
const retryButton = $<HTMLButtonElement>("retryButton");
const lockedCard = $<HTMLElement>("lockedCard");
const copyLinkButton = $<HTMLButtonElement>("copyLinkButton");
const backendPills = Array.from(
  document.querySelectorAll<HTMLButtonElement>(".vs-backend-pill"),
);

// --- State machine ----------------------------------------------------------

let currentState: State = "idle";
let lastPrompt = "";
let currentBackend: Backend = restoreBackend();

function restoreBackend(): Backend {
  try {
    const saved = localStorage.getItem(BACKEND_KEY);
    if (saved === "mlx" || saved === "codex") return saved;
  } catch {
    // localStorage disabled
  }
  return "mlx";
}

function setBackend(next: Backend): void {
  currentBackend = next;
  try {
    localStorage.setItem(BACKEND_KEY, next);
  } catch {
    // noop
  }
  for (const pill of backendPills) {
    const isActive = pill.dataset.backend === next;
    pill.setAttribute("aria-checked", String(isActive));
  }
}

function setState(next: State): void {
  currentState = next;
  main.dataset.state = next;

  revealSection.hidden = next !== "loading";
  resultCard.hidden = next !== "ready";
  errorCard.hidden = next !== "error";
  lockedCard.hidden = next !== "locked";
  promptCard.hidden = next === "locked";

  if (next === "idle") {
    promptInput.disabled = false;
    updateBuildButton();
  } else if (next === "loading") {
    promptInput.disabled = true;
    buildButton.disabled = true;
  } else if (next === "ready" || next === "error") {
    promptInput.disabled = false;
    updateBuildButton();
  }
}

// --- Non-Apple UA lockout ---------------------------------------------------

function isAppleDevice(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|Macintosh|Mac OS X/.test(ua);
}

if (!isAppleDevice()) {
  setState("locked");
  copyLinkButton.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      copyLinkButton.textContent = "Copied";
      setTimeout(() => {
        copyLinkButton.textContent = "Copy link to send";
      }, 2000);
    } catch {
      // noop — clipboard can fail in iframes
    }
  });
} else {
  bootApple();
}

function bootApple(): void {
  // Apply saved backend selection to the pill UI on first paint.
  setBackend(currentBackend);

  // Backend toggle — tap either pill to switch.
  for (const pill of backendPills) {
    pill.addEventListener("click", () => {
      const choice = pill.dataset.backend;
      if (choice === "mlx" || choice === "codex") setBackend(choice);
    });
  }

  // Restore last-used prompt as a sessionStorage ghost so a returning friend
  // doesn't stare at an empty box. Only fires within the same browser session,
  // drops on tab close. 24h result cache still drives result-card restoration.
  const GHOST_KEY = "vs-last-prompt-v1";
  try {
    const ghost = sessionStorage.getItem(GHOST_KEY);
    if (ghost && !promptInput.value) {
      promptInput.value = ghost;
    }
  } catch {
    // sessionStorage disabled in private mode
  }

  // Char counter wiring — trimmed length matches the validation used in
  // updateBuildButton, avoiding a counter/button divergence at whitespace edges.
  promptInput.addEventListener("input", () => {
    const len = promptInput.value.trim().length;
    charCounter.textContent = `${len} / ${MAX_CHARS}`;
    updateBuildButton();
    try {
      sessionStorage.setItem(GHOST_KEY, promptInput.value);
    } catch {
      // noop
    }
  });

  buildButton.addEventListener("click", () => {
    const prompt = promptInput.value.trim();
    if (!prompt) return;
    void runBuild(prompt);
  });

  // Cmd/Ctrl+Enter submits
  promptInput.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!buildButton.disabled) buildButton.click();
    }
  });

  retryButton.addEventListener("click", () => {
    if (lastPrompt) void runBuild(lastPrompt);
  });

  // Restore last result on refresh (24h cache)
  restoreLastBuild();

  // Focus the input on first paint on desktop. Mobile keyboards are disruptive
  // so skip auto-focus on touch devices.
  if (!matchMedia("(hover: none)").matches) {
    promptInput.focus();
  }
}

function updateBuildButton(): void {
  // 20-char minimum — "remind" at 6 chars was a fragment; 20 lets us catch
  // "remind me to drink water" class of prompts while blocking typos.
  const MIN = 20;
  const len = promptInput.value.trim().length;
  buildButton.disabled = len < MIN || len > MAX_CHARS || currentState === "loading";
}

// --- Build flow -------------------------------------------------------------

async function runBuild(prompt: string): Promise<void> {
  // A fresh build invalidates any cached result card.
  resultCard.removeAttribute("data-cached");
  lastPrompt = prompt;
  setState("loading");
  phaseText.textContent = "writing";
  chipRow.innerHTML = "";
  progress.hidden = false;
  setProgressPct(8);

  // Kick off scripted scaffolding chips; the real SSE phase events will
  // update phase text and progress as they arrive.
  scheduleScaffoldingChips();

  try {
    await buildFetchSSE(prompt);
  } catch (_err) {
    showError({
      status: "error",
      code: "network",
      retryable: true,
      message: "couldn't reach the builder. check your connection.",
    });
  }
}

// Progress bar state. Uses transform:scaleX so it runs on the compositor
// rather than triggering layout on every frame (perf-alpha P-03).
let progressPct = 0;

function setProgressPct(pct: number, options: { allowDecrease?: boolean } = {}): void {
  // Never retreat unless explicitly allowed. Prevents scaffolding → SSE phase
  // regressions from showing a backward animation (perf-beta P1).
  if (!options.allowDecrease && pct < progressPct) return;
  progressPct = pct;
  progressFill.style.transform = `scaleX(${pct / 100})`;
}

let scaffoldingTimeout: number | null = null;

function scheduleScaffoldingChips(): void {
  clearScaffolding();
  const labels: Array<{ label: string; category: "trigger" | "action" | "flow" }> = [
    { label: "trigger", category: "trigger" },
    { label: "action", category: "action" },
    { label: "flow", category: "flow" },
    { label: "end", category: "trigger" },
  ];
  let i = 0;
  const tick = () => {
    if (currentState !== "loading" || i >= labels.length) return;
    const { label, category } = labels[i]!;
    const chip = document.createElement("span");
    chip.className = "vs-chip";
    chip.dataset.category = category;
    chip.textContent = label;
    // Mark only the most recent chip as active (pulse animation).
    chipRow
      .querySelectorAll<HTMLElement>("[data-active='1']")
      .forEach((el) => el.removeAttribute("data-active"));
    chip.dataset.active = "1";
    chipRow.appendChild(chip);
    i++;
    setProgressPct(Math.min(45, 12 + i * 8));
    if (i < labels.length) {
      scaffoldingTimeout = window.setTimeout(tick, 650);
    }
  };
  tick();
}

function clearScaffolding(): void {
  if (scaffoldingTimeout !== null) {
    window.clearTimeout(scaffoldingTimeout);
    scaffoldingTimeout = null;
  }
}

async function buildFetchSSE(prompt: string): Promise<void> {
  const url = new URL(`${API_BASE}/api/build`);
  if (FRIEND_KEY) url.searchParams.set("k", FRIEND_KEY);
  const r = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "text/event-stream",
    },
    body: JSON.stringify({ prompt, backend: currentBackend }),
  });

  // Non-stream response (Worker returned an error before reaching the tunnel,
  // or the edge returned a one-shot JSON). Handle as a plain build response.
  const contentType = r.headers.get("content-type") || "";
  if (!r.body || !contentType.includes("text/event-stream")) {
    const body = (await r.json().catch(() => ({}))) as Partial<BuildResponse>;
    if (r.ok && body.status === "ok") {
      showResult(body as BuildOK);
      return;
    }
    showError({
      status: "error",
      code: (body as BuildError).code || `http_${r.status}`,
      retryable: (body as BuildError).retryable ?? r.status >= 500,
      message: (body as BuildError).message || `server returned ${r.status}`,
      retry_after: (body as BuildError).retry_after,
    });
    return;
  }

  const reader = r.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Parse complete events separated by blank lines
    while (true) {
      const idx = buffer.indexOf("\n\n");
      if (idx === -1) break;
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      handleSSEEvent(raw);
    }
  }
}

function handleSSEEvent(raw: string): void {
  let event = "";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
  }
  const data = dataLines.join("\n");
  if (event === "phase") {
    applyPhase(data);
  } else if (event === "result") {
    try {
      const parsed = JSON.parse(data) as BuildOK;
      showResult(parsed);
    } catch {
      showError({
        status: "error",
        code: "bad_stream",
        retryable: true,
        message: "stream garbled. try again.",
      });
    }
  } else if (event === "error") {
    try {
      const parsed = JSON.parse(data) as BuildError;
      showError(parsed);
    } catch {
      showError({
        status: "error",
        code: "bad_stream",
        retryable: true,
        message: "stream garbled. try again.",
      });
    }
  }
}

function applyPhase(phase: string): void {
  if (phase === "warming_up") {
    // Cold start. Friend sees this for up to 30s before anything else moves.
    phaseText.textContent = "warming up";
    setProgressPct(15);
  } else if (phase === "started") {
    phaseText.textContent = "writing";
    setProgressPct(50);
  } else if (phase === "compiling") {
    phaseText.textContent = "compiling";
    setProgressPct(80);
  } else if (phase === "ready") {
    phaseText.textContent = "almost there";
    setProgressPct(95);
  }
}

function showResult(r: BuildOK): void {
  setProgressPct(100);
  clearScaffolding();
  // Brief transition, let the progress bar fill visibly before swap
  window.setTimeout(() => {
    resultName.textContent = r.name;
    resultSummary.textContent = r.summary;
    resultActionCount.textContent =
      r.action_count === 1 ? "1 action" : `${r.action_count} actions`;
    // Protocol-safe: reject anything that isn't shortcuts:// or https://.
    installLink.href = safeHref(r.install_url, "https:");
    downloadLink.href = safeHref(r.shortcut_url, "https:");
    setState("ready");
    cacheLastBuild(r);
    // Ensure result is visible if the keyboard was previously open and
    // pushed it offscreen.
    resultCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, 220);
}

function safeHref(raw: string, expectedScheme: "shortcuts:" | "https:"): string {
  try {
    const u = new URL(raw);
    if (u.protocol === expectedScheme) return raw;
    if (expectedScheme === "https:" && u.protocol === "http:") return raw;
  } catch {
    // fall through
  }
  return "#";
}

function showError(r: BuildError): void {
  clearScaffolding();
  errorTitle.textContent = friendlyError(r);
  retryButton.hidden = !r.retryable;
  setState("error");
}

function friendlyError(r: BuildError): string {
  switch (r.code) {
    case "couldnt_build":
      return "didn't quite land that. try rephrasing?";
    case "builder_asleep":
      return "the builder's cold. give it 30 seconds and try again.";
    case "too_many":
      return r.retry_after
        ? `slow down a sec. try again in ${r.retry_after}s.`
        : "slow down a sec";
    case "unauthorized":
      return "this link needs a key. ask whoever sent it.";
    case "invalid_request":
      return "prompt must be 1 to 800 characters.";
    default:
      return r.message || "something's off on my side. try again later.";
  }
}

// --- Cache -----------------------------------------------------------------

interface CachedBuild extends BuildOK {
  cachedAt: number;
}

function cacheLastBuild(r: BuildOK): void {
  try {
    const entry: CachedBuild = { ...r, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage can be disabled in private mode — just skip
  }
}

function restoreLastBuild(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const entry = JSON.parse(raw) as CachedBuild;
    // 24h TTL matches R2 object lifecycle
    if (Date.now() - entry.cachedAt > 24 * 3600 * 1000) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }
    // Shape guard — prior schema versions may lack fields; drop rather than set href to "undefined"
    if (
      typeof entry.install_url !== "string" ||
      typeof entry.shortcut_url !== "string" ||
      typeof entry.name !== "string"
    ) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }
    resultName.textContent = entry.name;
    resultSummary.textContent = entry.summary;
    resultActionCount.textContent =
      entry.action_count === 1 ? "1 action" : `${entry.action_count} actions`;
    installLink.href = safeHref(entry.install_url, "https:");
    downloadLink.href = safeHref(entry.shortcut_url, "https:");
    // Mark as cached so the spring animation doesn't fire on restore.
    resultCard.setAttribute("data-cached", "1");
    setState("ready");
  } catch {
    // Corrupt cache — drop it
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // noop
    }
  }
}

// --- Config helpers --------------------------------------------------------

function resolveApiBase(): string {
  // Worker lives on its default .workers.dev hostname. Free-plan Cloudflare
  // Universal SSL doesn't cover two-level subdomains, so a branded API
  // custom domain (api.vectors.getanibble.com.au) requires Advanced
  // Certificate Manager. Not worth it for friend-phase v1 — the
  // workers.dev URL is already HTTPS, already CORS-approved, and stable.
  const envBase = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE;
  if (envBase) return envBase.replace(/\/$/, "");
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    return "http://localhost:8787"; // wrangler dev default
  }
  return "https://vectorshortcuts-worker.fk7cpqcysx.workers.dev";
}

function resolveFriendKey(): string | null {
  // Accept from ?k= query (pass-through) or baked-in VITE_FRIEND_KEY for convenience.
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get("k");
  if (fromQuery) return fromQuery;
  const envKey = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_FRIEND_KEY;
  return envKey ? envKey : null;
}
